import api from './client';
import type { Booking } from 'types/booking';

export interface SlotValidationError {
  code: string;
  message: string;
  /** false = never forceable (e.g. `presence_too_short`: car present less than the work). */
  forceable?: boolean;
}

/** True when every error of the validation can be overridden with « Forcer ». */
export const isForceable = (r: { errors: SlotValidationError[] }): boolean =>
  r.errors.every((e) => e.forceable !== false);

export interface SlotValidationResult {
  errors: SlotValidationError[];
  suggested_restitution_time: string | null;
  suggested_restitution_date: string | null;
  earliest_restitution_hour: number | null;
}

export interface SlotValidationPayload {
  org_id?: string;
  deposit_date: string;       // YYYY-MM-DD
  deposit_time: string;       // HH:MM
  service_type?: string;
  is_fast?: boolean;
  /** Only recall-campaign interventions selected → slot checked against the campagnes atelier zone. */
  campagne?: boolean;
  intervention_hours?: number | null;
  /** Hours actually estimated on the lines — what the non-forceable presence rule compares. */
  work_hours?: number | null;
  restitution_date?: string | null;
  restitution_time?: string | null;
  check_deposit?: boolean;
  /** Ordre being edited — excluded from the collision checks (its own desk is the lane tested). */
  exclude_ordre_id?: string;
  /** Column the RDV lands on (clicked on the planning): reception occupancy is tested on that lane only. */
  reception_category?: string;
}

// ── Availability response cache ──────────────────────────────────────────────
// The date step's full-days + per-day slot lookups are Firestore-heavy. Caching
// them briefly (and deduping in-flight calls) lets us prefetch them while the
// user is still on an earlier step, so the date step loads instantly instead of
// showing "Chargement des disponibilités…". Cleared whenever a booking is made.
const _AVAIL_TTL_MS = 60_000;
const _availCache = new Map<string, { ts: number; promise: Promise<unknown> }>();

function _cachedAvail<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const hit = _availCache.get(key);
  if (hit && Date.now() - hit.ts < _AVAIL_TTL_MS) return hit.promise as Promise<T>;
  const promise = fetcher().catch((e) => { _availCache.delete(key); throw e; });
  _availCache.set(key, { ts: Date.now(), promise });
  return promise;
}

export const bookingsApi = {
  list: async (params?: { org_id?: string; status?: string; limit?: number; offset?: number }): Promise<Booking[]> => {
    const { data } = await api.get('/bookings', { params });
    return data;
  },

  get: async (id: string): Promise<Booking> => {
    const { data } = await api.get(`/bookings/${id}`);
    return data;
  },

  // `mute_client` : prise de RDV interne uniquement — coupe TOUTES les communications
  // sortantes vers ce client jusqu'a reactivation manuelle depuis le chat.
  create: async (booking: Omit<Booking, 'id' | 'created_at' | 'status'> & { force?: boolean; internal?: boolean; mute_client?: boolean; owner_change?: boolean; internal_work?: boolean; arrived_by_assistance?: boolean }): Promise<Booking> => {
    const { data } = await api.post('/bookings', booking);
    _availCache.clear();  // a new booking changes availability — don't serve stale slots
    return data;
  },

  update: async (id: string, updates: Partial<Booking>): Promise<Booking> => {
    const { data } = await api.patch(`/bookings/${id}`, updates);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/bookings/${id}`);
  },

  getFullDays: async (month: string, orgId?: string, fast?: boolean, serviceType?: string, durationMinutes?: number, urgent?: boolean, campagne?: boolean): Promise<{ month: string; max_per_day: number; full_days: string[] }> => {
    const key = `fd:${month}|${orgId || ""}|${fast ? 1 : 0}|${serviceType || ""}|${durationMinutes || 0}|${urgent ? 1 : 0}|${campagne ? 1 : 0}`;
    return _cachedAvail(key, async () => {
      const { data } = await api.get('/bookings/full-days', { params: { month, org_id: orgId, ...(fast ? { fast: true } : {}), ...(serviceType ? { service_type: serviceType } : {}), ...(durationMinutes ? { duration_minutes: durationMinutes } : {}), ...(urgent ? { urgent: true } : {}), ...(campagne ? { campagne: true } : {}) } });
      return data;
    });
  },

  getAvailability: async (date: string, serviceType: string, orgId?: string, fast?: boolean, durationMinutes?: number, urgent?: boolean, campagne?: boolean): Promise<{ date: string; service_type: string; unavailable_slots: string[]; estimated_ready_times?: Record<string, string>; unavailable_reasons?: Record<string, string>; estimated_placements?: Record<string, { worker_id: string; start_hour: number; ready_hour: number; reception_category?: string; restitution_category?: string }> }> => {
    const key = `av:${date}|${serviceType}|${orgId || ""}|${fast ? 1 : 0}|${durationMinutes || 0}|${urgent ? 1 : 0}|${campagne ? 1 : 0}`;
    return _cachedAvail(key, async () => {
      const { data } = await api.get('/bookings/availability', { params: { date, service_type: serviceType, org_id: orgId, ...(fast ? { fast: true } : {}), ...(durationMinutes ? { duration_minutes: durationMinutes } : {}), ...(urgent ? { urgent: true } : {}), ...(campagne ? { campagne: true } : {}) } });
      return data;
    });
  },

  /** Validate a manual (staff) RDV slot with the same rules as the online widget. Non-raising. */
  validateSlot: async (payload: SlotValidationPayload): Promise<SlotValidationResult> => {
    const { data } = await api.post('/bookings/validate-slot', payload);
    return data;
  },

  // The restitution runs on the same reception desk as the drop-off, so its date and
  // hour have their own availability — otherwise the step offers days and slots where
  // no desk is free and the handover lands on a busy column.
  getRestitutionFullDays: async (month: string, orgId?: string, depositDate?: string | null, depositTime?: string, serviceType?: string, excludeOrdreId?: string): Promise<{ month: string; full_days: string[] }> => {
    const key = `rfd:${month}|${orgId || ""}|${depositDate || ""}|${depositTime || ""}|${serviceType || ""}|${excludeOrdreId || ""}`;
    return _cachedAvail(key, async () => {
      const { data } = await api.get('/bookings/restitution-full-days', { params: { month, org_id: orgId, ...(depositDate ? { deposit_date: depositDate } : {}), ...(depositTime ? { deposit_time: depositTime } : {}), ...(serviceType ? { service_type: serviceType } : {}), ...(excludeOrdreId ? { exclude_ordre_id: excludeOrdreId } : {}) } });
      return data;
    });
  },

  getRestitutionAvailability: async (date: string, orgId?: string, depositDate?: string | null, depositTime?: string, serviceType?: string, excludeOrdreId?: string): Promise<{ date: string; closed?: boolean; unavailable_slots: string[]; unavailable_reasons?: Record<string, string>; lanes?: Record<string, string> }> => {
    const key = `rav:${date}|${orgId || ""}|${depositDate || ""}|${depositTime || ""}|${serviceType || ""}|${excludeOrdreId || ""}`;
    return _cachedAvail(key, async () => {
      const { data } = await api.get('/bookings/restitution-availability', { params: { date, org_id: orgId, ...(depositDate ? { deposit_date: depositDate } : {}), ...(depositTime ? { deposit_time: depositTime } : {}), ...(serviceType ? { service_type: serviceType } : {}), ...(excludeOrdreId ? { exclude_ordre_id: excludeOrdreId } : {}) } });
      return data;
    });
  },

  lookupClient: async (phone: string, orgId?: string): Promise<{ found: boolean; dossier_id?: string; nom?: string; prenom?: string; email?: string; phone?: string; adresse?: string; client_muted?: boolean; client_muted_at?: string | null; client_muted_by_name?: string | null; client_muted_reason?: string | null }> => {
    const { data } = await api.get('/bookings/lookup-client', { params: { phone, org_id: orgId } });
    return data;
  },

  lookupVehicle: async (plate: string, orgId?: string): Promise<{ found: boolean; error?: string; vehicle_id?: string; plate?: string; marque?: string; modele?: string; km?: number; mec?: number; image_url?: string; dossier_id?: string; client_name?: string; client_phone?: string }> => {
    const { data } = await api.get('/bookings/lookup-vehicle', { params: { plate, org_id: orgId } });
    return data;
  },

  searchVehicles: async (query: string, orgId?: string): Promise<{ plate: string; marque: string; modele: string; vehicle_id?: string; dossier_id?: string; client_name?: string; image_url?: string }[]> => {
    const { data } = await api.get('/bookings/search-vehicles', { params: { q: query, org_id: orgId } });
    return data;
  },
};
